import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CheckIcon } from "../../components/icons";
import { firstStepsQuery } from "../../lib/queries";

export interface FirstStep {
  done: boolean;
  label: string;
  to: "/bienvenida" | "/empresa" | "/catalogo" | "/conocimiento" | "/lumi";
  search?: { tab: "horario" };
}

/**
 * What is still missing for Lumi to answer well, in order; null while loading or for
 * agents (the setup is the owners' and admins').
 */
export function useFirstSteps(enabled = true) {
  const flags = useQuery({ ...firstStepsQuery, enabled }).data;
  if (!enabled || !flags) return null;
  const steps: FirstStep[] = [
    { done: flags.setup, label: "Responde el recorrido de bienvenida", to: "/bienvenida" },
    { done: flags.description, label: "Cuenta qué hace la empresa", to: "/empresa" },
    { done: flags.hours, label: "Pon el horario de atención", to: "/empresa", search: { tab: "horario" } },
    { done: flags.catalog, label: "Agrega tus productos o servicios", to: "/catalogo" },
    { done: flags.knowledge, label: "Sube garantías, manuales o preguntas frecuentes", to: "/conocimiento" },
    { done: flags.lumiOn, label: "Prueba Lumi y enciéndela", to: "/lumi" },
  ];
  const done = steps.filter((s) => s.done).length;
  return { steps, done, total: steps.length, next: steps.find((s) => !s.done) ?? null };
}

/** "Primeros pasos" on the Lumi page: the list with a link to each step. Hidden once all are done. */
export function FirstSteps() {
  const state = useFirstSteps();
  if (!state?.next) return null;
  const { steps, done, total, next } = state;

  return (
    <div className="card first-steps" id="primeros-pasos">
      <div className="card-body">
        <div className="account-head">
          <div>
            <h3>Primeros pasos</h3>
            <p className="muted">
              {done} de {total} listos. Con todo esto, Lumi responde bien desde el primer mensaje.
            </p>
          </div>
          {next.to !== "/lumi" && (
            <Link className="btn btn-primary" to={next.to} search={next.search ?? {}}>
              Seguir
            </Link>
          )}
        </div>
        <div className="setup-progress" aria-hidden="true">
          <span style={{ transform: `scaleX(${done / total})` }} />
        </div>
        <ol className="first-steps-list">
          {steps.map((s) => (
            <li key={s.label} className={s.done ? "done" : undefined}>
              <span className="first-steps-mark" aria-hidden="true">
                {s.done && <CheckIcon />}
              </span>
              {s.done ? (
                <span>{s.label}</span>
              ) : (
                <Link to={s.to} search={s.search ?? {}}>
                  {s.label}
                </Link>
              )}
              <span className="sr-only">{s.done ? " (listo)" : " (pendiente)"}</span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
